// Custom plan params travel from a shared starter-pay link through the new
// get-started flow: get-started-account → email-verify → get-started-payment.
// We persist them to localStorage on the account step so they survive the
// email round-trip on the same device, AND append them to the email's
// redirect_path so a cross-device click still recovers them.
export const CUSTOM_PLAN_PARAMS = [
  "label",
  "billed",
  "today_label",
  "thereafter",
  "show_platform_qualifier",
  "onboarding",
  "platform",
  "trust",
  "will",
  "commitment",
  "promo",
  "promo_terms",
  "promo_badge",
] as const;

export type CustomPlanParam = (typeof CUSTOM_PLAN_PARAMS)[number];

const STORAGE_KEY = "snug.starter-pay.custom-plan-params";

export function extractCustomPlanFromUrl(): Record<string, string> | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const out: Record<string, string> = {};
  for (const p of CUSTOM_PLAN_PARAMS) {
    const v = params.get(p);
    if (v != null) out[p] = v;
  }
  return Object.keys(out).length ? out : null;
}

export function writeCustomPlanParams(params: Record<string, string>): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(params));
  } catch {
    // localStorage unavailable (private mode, quota); fall back to email round-trip
  }
}

export function readCustomPlanParams(): Record<string, string> | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    return parsed as Record<string, string>;
  } catch {
    return null;
  }
}

export function clearCustomPlanParams(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

// Serialize a stored custom-plan record back to a `?key=value&...` string so
// it can be appended to a path. Returns "" when there are no params.
export function customPlanQueryString(
  params: Record<string, string> | null,
): string {
  if (!params) return "";
  const sp = new URLSearchParams();
  for (const k of CUSTOM_PLAN_PARAMS) {
    const v = params[k];
    if (v) sp.set(k, v);
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}
